import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { useUserRole } from "@/hooks/use-user-role";
import { getAuthenticatedLocalClient } from "@/lib/supabase-clients";

type AclSource = "pricelabs" | "guesty";

function getLocalUserIdFromToken(localToken: string): string | null {
  try {
    const payload = JSON.parse(atob(localToken.split('.')[1]));
    return payload.sub || null;
  } catch {
    return null;
  }
}

// Pick the most common 'pricelabs'/'guesty' value across ACL rows; ignore nulls
// and unknown labels. Returns undefined if no usable source is present.
function pickDominantSource(rows: Array<{ source: string | null | undefined }>): AclSource | undefined {
  let pl = 0, gu = 0;
  for (const r of rows) {
    if (r.source === "pricelabs") pl++;
    else if (r.source === "guesty") gu++;
  }
  if (pl === 0 && gu === 0) return undefined;
  return pl > gu ? "pricelabs" : "guesty";
}

/**
 * Returns the set of owner codes (client flow) AND/OR the set of listing
 * guesty_ids (owner flow) this user can access.
 *
 * - super_admin                          → both null (= ALL)
 * - user_owner_access (client flow)      → allowedClientCodes = Set<client_code>
 * - user_listing_access (owner flow)     → allowedListingIds = Set<guesty_id>
 *
 * The two paths are independent and additive at the RLS level.
 */
export function useUserListingAccess(opts: { includeListingIds?: boolean } = {}) {
  const includeListingIds = opts.includeListingIds === true;
  const { user, localToken } = useAuth();
  const { isSuperAdmin, isAdmin, roles, isLoading: roleLoading } = useUserRole();
  const localUserId = localToken ? getLocalUserIdFromToken(localToken) : null;
  const isOwner = roles.includes("owner") || roles.includes("owner_test");
  // Admin (incl. super_admin) gets unrestricted client access when no explicit
  // user_owner_access rows are configured — same fallback for both roles.
  const isUnrestrictedFallback = isSuperAdmin || isAdmin;

  const {
    data: ownerAccessResult = {
      allowedClientCodes: null as Set<string> | null,
      defaultClientSource: undefined as AclSource | undefined,
    },
    isLoading: accessLoading,
    isSuccess: accessSuccess,
  } = useQuery({
    queryKey: ["user_owner_access", localUserId, isUnrestrictedFallback, isOwner],
    queryFn: async (): Promise<{
      allowedClientCodes: Set<string> | null;
      defaultClientSource: AclSource | undefined;
    }> => {
      // Owner role uses user_listing_access exclusively — never read user_owner_access.
      if (isOwner && !isUnrestrictedFallback) {
        return { allowedClientCodes: new Set<string>(), defaultClientSource: undefined };
      }
      const client = getAuthenticatedLocalClient();
      if (!client || !localUserId) {
        return {
          allowedClientCodes: isUnrestrictedFallback ? null : new Set<string>(),
          defaultClientSource: undefined,
        };
      }

      const { data, error } = await client
        .from("user_owner_access")
        .select("client_code, source")
        .eq("user_id", localUserId);
      if (error) throw error;

      // If user has explicit client_code entries, enforce them (even for admins).
      // Normalize to uppercase — CRM property.client_code is uppercase and
      // PostgREST `.in()` is case-sensitive, so mixed-case rows in
      // user_owner_access would otherwise match zero properties.
      if (data && data.length > 0) {
        const rawCodes = data.map((r: any) => r.client_code as string);
        const defaultClientSource = pickDominantSource(data as any);
        // Sentinel "__ALL__" grants access to every owner code
        if (rawCodes.includes("__ALL__")) return { allowedClientCodes: null, defaultClientSource };
        const codes = rawCodes.map((c) => c.toUpperCase());
        return { allowedClientCodes: new Set(codes), defaultClientSource };
      }

      // No entries: admin / super_admin get all access, others get none
      return {
        allowedClientCodes: isUnrestrictedFallback ? null : new Set<string>(),
        defaultClientSource: undefined,
      };
    },
    enabled: !!user && !!localToken && !!localUserId && !roleLoading,
    staleTime: 5 * 60 * 1000,
  });

  const {
    data: listingAccess = {
      propertyIds: null as Set<string> | null,
      listingIds: null as Set<string> | null,
      defaultListingSource: undefined as AclSource | undefined,
    },
    isLoading: listingsLoading,
    isSuccess: listingsSuccess,
  } = useQuery({
    queryKey: ["user_listing_access", localUserId, isSuperAdmin, isOwner, includeListingIds],
    queryFn: async (): Promise<{
      propertyIds: Set<string> | null;
      listingIds: Set<string> | null;
      defaultListingSource: AclSource | undefined;
    }> => {
      // super_admin / __ALL__ → unrestricted; null sentinel on both axes
      if (isSuperAdmin) return { propertyIds: null, listingIds: null, defaultListingSource: undefined };
      // Only the 'owner' role uses the per-listing access table
      if (!isOwner) return { propertyIds: null, listingIds: null, defaultListingSource: undefined };
      const client = getAuthenticatedLocalClient();
      if (!client || !localUserId) {
        return { propertyIds: new Set<string>(), listingIds: new Set<string>(), defaultListingSource: undefined };
      }

      const { data: access, error } = await client
        .from("user_listing_access")
        .select("property_id, source")
        .eq("user_id", localUserId);
      if (error) throw error;

      const defaultListingSource = pickDominantSource((access ?? []) as any);
      const propertyIds = new Set<string>(
        (access ?? [])
          .map((r: any) => r.property_id as string | null)
          .filter((p): p is string => !!p),
      );
      if (propertyIds.size === 0) return { propertyIds, listingIds: new Set<string>(), defaultListingSource };

      // Owner-flow listing_id translation is only needed by the Reservations
      // page (via ReservationsContext). Every other caller — including the
      // Performance page — must NOT touch the local pg_guesty_listing mirror.
      if (!includeListingIds) {
        return { propertyIds, listingIds: null, defaultListingSource };
      }

      const { data: listings, error: lErr } = await client
        .from("pg_guesty_listing")
        .select("guesty_id")
        .in("property_id", Array.from(propertyIds));
      if (lErr) throw lErr;

      const listingIds = new Set<string>((listings ?? []).map((r: any) => r.guesty_id as string));
      return { propertyIds, listingIds, defaultListingSource };
    },
    enabled: !!user && !!localToken && !!localUserId && !roleLoading,
    staleTime: 5 * 60 * 1000,
  });

  return {
    /** null = all access (super_admin / __ALL__), Set = restricted to client_codes */
    allowedClientCodes: ownerAccessResult.allowedClientCodes,
    /** null = no per-listing restriction (non-owner or super_admin), Set = restricted to listing guesty_ids */
    allowedListingIds: listingAccess.listingIds,
    /** null = no per-property restriction (non-owner or super_admin), Set = restricted to property_ids */
    allowedPropertyIds: listingAccess.propertyIds,
    /** Dominant source recorded on user_owner_access rows (client flow). */
    defaultClientSource: ownerAccessResult.defaultClientSource,
    /** Dominant source recorded on user_listing_access rows (owner flow). */
    defaultListingSource: listingAccess.defaultListingSource,
    isSuperAdmin,
    isOwner,
    isLoading: roleLoading || accessLoading || listingsLoading,
    /** true only when role + both access queries have resolved */
    isReady: !roleLoading && accessSuccess && listingsSuccess,
  };
}
